import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { CompaniesAdminTable } from "@/components/companies-admin/CompaniesAdminTable";
import { CompanyAdminModal } from "@/components/companies-admin/CompanyAdminModal";
import { CompanyAdminFilters } from "@/components/companies-admin/CompanyAdminFilters";
import { useCompaniesAdmin } from "@/hooks/useCompaniesAdmin";
import { CompanyAdmin, CompanyAdminInput } from "@/types/crm";
import { useToast } from "@/hooks/use-toast";

export default function CompaniesAdmin() {
  const {
    filteredCompanies,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    createCompany,
    updateCompany,
    deleteCompany,
    statistics
  } = useCompaniesAdmin();

  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyAdmin | null>(null);

  const handleCreate = () => {
    setEditingCompany(null);
    setIsModalOpen(true);
  };

  const handleEdit = (company: CompanyAdmin) => {
    setEditingCompany(company);
    setIsModalOpen(true);
  };

  const handleSave = (companyData: CompanyAdminInput) => {
    try {
      if (editingCompany) {
        updateCompany(editingCompany.id, companyData);
        toast({
          title: "Empresa actualizada",
          description: `${companyData.name} ha sido actualizada exitosamente`,
        });
      } else {
        createCompany(companyData);
        toast({
          title: "Empresa creada",
          description: `${companyData.name} ha sido creada exitosamente`,
        });
      }
      setIsModalOpen(false);
      setEditingCompany(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Hubo un problema al guardar la empresa",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (company: CompanyAdmin) => {
    try {
      deleteCompany(company);
      toast({
        title: "Empresa desactivada",
        description: `${company.name} ha sido desactivada`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Hubo un problema al desactivar la empresa",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestión de Empresas</h1>
          <p className="text-slate-600">Administra las empresas del sistema</p>
        </div>
        <Button 
          onClick={handleCreate} 
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Empresa
        </Button>
      </div>

      <CompanyAdminFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        statistics={statistics}
      />

      <CompaniesAdminTable
        companies={filteredCompanies}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <CompanyAdminModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCompany(null);
        }}
        onSave={handleSave}
        company={editingCompany}
      />
    </div>
  );
}